import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Users,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  ArrowLeft,
  User,
  Shield,
  Loader2,
  RefreshCw
} from 'lucide-react'
import { useNotification } from '../../contexts/NotificationContext'

interface User {
  id: string
  name: string
  email: string
  role: 'admin' | 'manager' | 'user' | 'viewer'
  active: boolean
  last_login?: string
  created_at: string
  updated_at: string
}

const API_BASE = import.meta.env.VITE_API_URL || ''

export default function UsersPage() {
  const navigate = useNavigate()
  const { success, error: showError } = useNotification()
  const [users, setUsers] = useState<User[]>([])
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'user' as User['role'],
    active: true
  })
  const [loading, setLoading] = useState(false)
  const [refreshing, setRefreshing] = useState(false)

  const fetchUsers = async () => {
    try {
      setRefreshing(true)
      const response = await fetch(`${API_BASE}/api/users-pg`)
      const data = await response.json()

      if (data.success) {
        setUsers(data.data)
      } else {
        showError('Failed to fetch users', data.error)
      }
    } catch (err) {
      console.error('Error fetching users:', err)
      showError('Error', 'Failed to fetch users')
    } finally {
      setRefreshing(false)
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [])

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.name.trim() || !formData.email.trim()) {
      showError('Validation Error', 'Name and email are required')
      return
    }

    setLoading(true)

    try {
      const response = await fetch(`${API_BASE}/api/users-pg`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      })

      const data = await response.json()

      if (data.success) {
        success('Success', 'User created successfully')
        setUsers(prev => [...prev, data.data])
        setFormData({ name: '', email: '', role: 'user', active: true })
        setShowCreateDialog(false)
      } else {
        showError('Error', data.error)
      }
    } catch (err) {
      console.error('Error creating user:', err)
      showError('Error', 'Failed to create user')
    } finally {
      setLoading(false)
    }
  }

  const handleEditUser = (user: User) => {
    setEditingUser(user)
    setFormData({
      name: user.name,
      email: user.email,
      role: user.role,
      active: user.active
    })
  }

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!editingUser) return

    setLoading(true)

    try {
      const response = await fetch(`${API_BASE}/api/users-pg`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: editingUser.id, ...formData })
      })

      const data = await response.json()

      if (data.success) {
        success('Success', 'User updated successfully')
        setUsers(prev => prev.map(user =>
          user.id === editingUser.id ? data.data : user
        ))
        setEditingUser(null)
        setFormData({ name: '', email: '', role: 'user', active: true })
      } else {
        showError('Error', data.error)
      }
    } catch (err) {
      console.error('Error updating user:', err)
      showError('Error', 'Failed to update user')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteUser = async (userId: string) => {
    if (!confirm('Are you sure you want to delete this user?')) return

    try {
      const response = await fetch(`${API_BASE}/api/users-pg?id=${userId}`, {
        method: 'DELETE'
      })

      const data = await response.json()

      if (data.success) {
        success('Success', 'User deleted successfully')
        setUsers(prev => prev.filter(user => user.id !== userId))
      } else {
        showError('Error', data.error)
      }
    } catch (err) {
      console.error('Error deleting user:', err)
      showError('Error', 'Failed to delete user')
    }
  }

  const getRoleColor = (role: User['role']) => {
    const colors = {
      admin: '#FF6B35',    // Orange
      manager: '#FFD93D',  // Yellow
      user: '#00F0FF',     // Cyan
      viewer: '#8A95AA'    // Gray
    }
    return colors[role]
  }

  const getRoleIcon = (role: User['role']) => {
    const icons = {
      admin: Shield,
      manager: Users,
      user: User,
      viewer: User
    }
    return icons[role]
  }

  return (
    <div style={{
      padding: '24px',
      background: 'linear-gradient(180deg, #1A1F2E 0%, #242938 100%)',
      minHeight: '100vh',
      fontFamily: 'Rajdhani, sans-serif'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '32px',
        paddingBottom: '24px',
        borderBottom: '2px solid rgba(255, 107, 53, 0.3)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={() => navigate('/admin')}
            style={{
              background: 'rgba(138, 149, 170, 0.1)',
              border: '2px solid rgba(138, 149, 170, 0.3)',
              borderRadius: '6px',
              padding: '12px 20px',
              color: '#8A95AA',
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '14px',
              fontWeight: 700,
              letterSpacing: '1px',
              textTransform: 'uppercase',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(138, 149, 170, 0.2)'
              e.currentTarget.style.boxShadow = '0 0 15px rgba(138, 149, 170, 0.3)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(138, 149, 170, 0.1)'
              e.currentTarget.style.boxShadow = 'none'
            }}
          >
            <ArrowLeft style={{ width: 16, height: 16 }} />
            BACK
          </button>
          <h1 style={{
            fontSize: '48px',
            fontWeight: '700',
            textTransform: 'uppercase',
            letterSpacing: '2px',
            color: '#FF6B35',
            textShadow: '0 0 20px rgba(255, 107, 53, 0.5)',
            margin: 0
          }}>
            USERS
          </h1>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={fetchUsers}
            disabled={refreshing}
            style={{
              background: 'rgba(0, 240, 255, 0.1)',
              border: '2px solid rgba(0, 240, 255, 0.4)',
              borderRadius: '6px',
              padding: '12px 20px',
              color: '#00F0FF',
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '14px',
              fontWeight: 700,
              letterSpacing: '1px',
              textTransform: 'uppercase',
              cursor: refreshing ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s',
              opacity: refreshing ? 0.5 : 1
            }}
            onMouseEnter={(e) => {
              if (!refreshing) {
                e.currentTarget.style.background = 'rgba(0, 240, 255, 0.2)'
                e.currentTarget.style.boxShadow = '0 0 15px rgba(0, 240, 255, 0.3)'
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(0, 240, 255, 0.1)'
              e.currentTarget.style.boxShadow = 'none'
            }}
          >
            <RefreshCw style={{ width: 16, height: 16 }} className={refreshing ? 'animate-spin' : ''} />
            REFRESH
          </button>
          <button
            onClick={() => setShowCreateDialog(true)}
            style={{
              background: 'rgba(107, 207, 127, 0.1)',
              border: '2px solid rgba(107, 207, 127, 0.4)',
              borderRadius: '6px',
              padding: '12px 20px',
              color: '#6BCF7F',
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '14px',
              fontWeight: 700,
              letterSpacing: '1px',
              textTransform: 'uppercase',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(107, 207, 127, 0.2)'
              e.currentTarget.style.boxShadow = '0 0 15px rgba(107, 207, 127, 0.3)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(107, 207, 127, 0.1)'
              e.currentTarget.style.boxShadow = 'none'
            }}
          >
            <Plus style={{ width: 16, height: 16 }} />
            NEW USER
          </button>
        </div>
      </div>

      {/* Users List */}
      <div style={{ marginTop: '24px' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
          gap: '20px'
        }}>
          {users.map((user) => {
            const roleColor = getRoleColor(user.role)
            const RoleIcon = getRoleIcon(user.role)

            return (
              <div
                key={user.id}
                style={{
                  background: 'linear-gradient(135deg, rgba(26, 31, 46, 0.95) 0%, rgba(36, 41, 56, 0.95) 100%)',
                  border: `2px solid ${roleColor}40`,
                  borderRadius: '8px',
                  padding: '24px',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = roleColor
                  e.currentTarget.style.boxShadow = `0 0 20px ${roleColor}80`
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = `${roleColor}40`
                  e.currentTarget.style.boxShadow = 'none'
                }}
              >
                {/* Header with name and role icon */}
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  marginBottom: '20px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      background: `${roleColor}20`,
                      border: `1px solid ${roleColor}40`,
                      borderRadius: '8px',
                      padding: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <RoleIcon style={{ width: 24, height: 24, color: roleColor }} />
                    </div>
                    <div>
                      <div style={{
                        fontFamily: 'Rajdhani, sans-serif',
                        fontSize: '20px',
                        fontWeight: 700,
                        color: '#FFFFFF',
                        marginBottom: '4px'
                      }}>{user.name}</div>
                      <div style={{
                        fontFamily: 'Rajdhani, monospace',
                        fontSize: '13px',
                        color: '#8A95AA'
                      }}>{user.email}</div>
                    </div>
                  </div>
                </div>

                {/* Multi-colored stat blocks */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '12px',
                  marginBottom: '20px'
                }}>
                  {/* Role */}
                  <div style={{
                    background: `${roleColor}10`,
                    border: `1px solid ${roleColor}30`,
                    borderRadius: '6px',
                    padding: '10px'
                  }}>
                    <div style={{
                      fontFamily: 'Rajdhani, sans-serif',
                      fontSize: '10px',
                      color: '#8A95AA',
                      marginBottom: '4px',
                      letterSpacing: '1px',
                      textTransform: 'uppercase'
                    }}>ROLE</div>
                    <div style={{
                      fontFamily: 'Rajdhani, sans-serif',
                      fontSize: '16px',
                      fontWeight: 700,
                      color: roleColor,
                      textTransform: 'uppercase'
                    }}>{user.role}</div>
                  </div>

                  {/* Status */}
                  <div style={{
                    background: user.active ? 'rgba(107, 207, 127, 0.1)' : 'rgba(138, 149, 170, 0.1)',
                    border: user.active ? '1px solid rgba(107, 207, 127, 0.3)' : '1px solid rgba(138, 149, 170, 0.3)',
                    borderRadius: '6px',
                    padding: '10px'
                  }}>
                    <div style={{
                      fontFamily: 'Rajdhani, sans-serif',
                      fontSize: '10px',
                      color: '#8A95AA',
                      marginBottom: '4px',
                      letterSpacing: '1px',
                      textTransform: 'uppercase'
                    }}>STATUS</div>
                    <div style={{
                      fontFamily: 'Rajdhani, sans-serif',
                      fontSize: '16px',
                      fontWeight: 700,
                      color: user.active ? '#6BCF7F' : '#8A95AA',
                      textTransform: 'uppercase'
                    }}>{user.active ? 'ACTIVE' : 'INACTIVE'}</div>
                  </div>

                  {/* Last Login */}
                  <div style={{
                    background: 'rgba(0, 240, 255, 0.1)',
                    border: '1px solid rgba(0, 240, 255, 0.3)',
                    borderRadius: '6px',
                    padding: '10px'
                  }}>
                    <div style={{
                      fontFamily: 'Rajdhani, sans-serif',
                      fontSize: '10px',
                      color: '#8A95AA',
                      marginBottom: '4px',
                      letterSpacing: '1px',
                      textTransform: 'uppercase'
                    }}>LAST LOGIN</div>
                    <div style={{
                      fontFamily: 'Rajdhani, monospace',
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#00F0FF'
                    }}>{user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}</div>
                  </div>

                  {/* Created */}
                  <div style={{
                    background: 'rgba(185, 27, 255, 0.1)',
                    border: '1px solid rgba(185, 27, 255, 0.3)',
                    borderRadius: '6px',
                    padding: '10px'
                  }}>
                    <div style={{
                      fontFamily: 'Rajdhani, sans-serif',
                      fontSize: '10px',
                      color: '#8A95AA',
                      marginBottom: '4px',
                      letterSpacing: '1px',
                      textTransform: 'uppercase'
                    }}>CREATED</div>
                    <div style={{
                      fontFamily: 'Rajdhani, monospace',
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#B91BFF'
                    }}>{new Date(user.created_at).toLocaleDateString()}</div>
                  </div>
                </div>

                {/* Action buttons */}
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  paddingTop: '16px',
                  borderTop: '1px solid rgba(138, 149, 170, 0.2)'
                }}>
                  <button
                    onClick={() => handleEditUser(user)}
                    disabled={loading}
                    style={{
                      flex: 1,
                      background: 'rgba(255, 215, 61, 0.1)',
                      border: '2px solid rgba(255, 215, 61, 0.4)',
                      borderRadius: '6px',
                      padding: '10px 16px',
                      color: '#FFD93D',
                      fontFamily: 'Rajdhani, sans-serif',
                      fontSize: '13px',
                      fontWeight: 700,
                      letterSpacing: '1px',
                      textTransform: 'uppercase',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      transition: 'all 0.2s',
                      opacity: loading ? 0.5 : 1
                    }}
                    onMouseEnter={(e) => {
                      if (!loading) {
                        e.currentTarget.style.background = 'rgba(255, 215, 61, 0.2)'
                        e.currentTarget.style.boxShadow = '0 0 15px rgba(255, 215, 61, 0.3)'
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 215, 61, 0.1)'
                      e.currentTarget.style.boxShadow = 'none'
                    }}
                  >
                    <Edit style={{ width: 14, height: 14 }} />
                    EDIT
                  </button>
                  <button
                    onClick={() => handleDeleteUser(user.id)}
                    disabled={loading}
                    style={{
                      background: 'rgba(239, 68, 68, 0.1)',
                      border: '2px solid rgba(239, 68, 68, 0.4)',
                      borderRadius: '6px',
                      padding: '10px 16px',
                      color: '#EF4444',
                      fontFamily: 'Rajdhani, sans-serif',
                      fontSize: '13px',
                      fontWeight: 700,
                      cursor: loading ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s',
                      opacity: loading ? 0.5 : 1
                    }}
                    onMouseEnter={(e) => {
                      if (!loading) {
                        e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)'
                        e.currentTarget.style.boxShadow = '0 0 15px rgba(239, 68, 68, 0.3)'
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'
                      e.currentTarget.style.boxShadow = 'none'
                    }}
                  >
                    <Trash2 style={{ width: 14, height: 14 }} />
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Create/Edit User Dialog */}
      {(showCreateDialog || editingUser) && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 50,
          padding: '16px'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, rgba(26, 31, 46, 0.98) 0%, rgba(36, 41, 56, 0.98) 100%)',
            border: '2px solid rgba(255, 107, 53, 0.5)',
            borderRadius: '12px',
            padding: '32px',
            width: '100%',
            maxWidth: '500px',
            boxShadow: '0 0 40px rgba(255, 107, 53, 0.3)'
          }}>
            {/* Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '24px',
              paddingBottom: '16px',
              borderBottom: '1px solid rgba(255, 107, 53, 0.3)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '24px',
                fontWeight: 700,
                color: '#FF6B35',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                {editingUser ? (
                  <>
                    <Edit style={{ width: 20, height: 20 }} />
                    EDIT USER
                  </>
                ) : (
                  <>
                    <Plus style={{ width: 20, height: 20 }} />
                    CREATE NEW USER
                  </>
                )}
              </div>
              <button
                onClick={() => {
                  setShowCreateDialog(false)
                  setEditingUser(null)
                  setFormData({ name: '', email: '', role: 'user', active: true })
                }}
                style={{
                  background: 'rgba(138, 149, 170, 0.1)',
                  border: '1px solid rgba(138, 149, 170, 0.3)',
                  borderRadius: '6px',
                  padding: '8px',
                  color: '#8A95AA',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(138, 149, 170, 0.2)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(138, 149, 170, 0.1)'
                }}
              >
                <X style={{ width: 16, height: 16 }} />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={editingUser ? handleUpdateUser : handleCreateUser}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {/* Full Name */}
                <div>
                  <label style={{
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '11px',
                    color: '#8A95AA',
                    marginBottom: '8px',
                    display: 'block',
                    letterSpacing: '1px',
                    textTransform: 'uppercase'
                  }}>FULL NAME *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Enter full name"
                    required
                    style={{
                      width: '100%',
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(138, 149, 170, 0.3)',
                      borderRadius: '6px',
                      padding: '12px',
                      color: '#FFFFFF',
                      fontFamily: 'Rajdhani, sans-serif',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'all 0.2s'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#FF6B35'
                      e.currentTarget.style.boxShadow = '0 0 0 2px rgba(255, 107, 53, 0.1)'
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(138, 149, 170, 0.3)'
                      e.currentTarget.style.boxShadow = 'none'
                    }}
                  />
                </div>

                {/* Email */}
                <div>
                  <label style={{
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '11px',
                    color: '#8A95AA',
                    marginBottom: '8px',
                    display: 'block',
                    letterSpacing: '1px',
                    textTransform: 'uppercase'
                  }}>EMAIL *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="user@example.com"
                    required
                    style={{
                      width: '100%',
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(138, 149, 170, 0.3)',
                      borderRadius: '6px',
                      padding: '12px',
                      color: '#FFFFFF',
                      fontFamily: 'Rajdhani, sans-serif',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'all 0.2s'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#FF6B35'
                      e.currentTarget.style.boxShadow = '0 0 0 2px rgba(255, 107, 53, 0.1)'
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(138, 149, 170, 0.3)'
                      e.currentTarget.style.boxShadow = 'none'
                    }}
                  />
                </div>

                {/* Role */}
                <div>
                  <label style={{
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '11px',
                    color: '#8A95AA',
                    marginBottom: '8px',
                    display: 'block',
                    letterSpacing: '1px',
                    textTransform: 'uppercase'
                  }}>ROLE</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value as User['role'] }))}
                    style={{
                      width: '100%',
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(138, 149, 170, 0.3)',
                      borderRadius: '6px',
                      padding: '12px',
                      color: '#FFFFFF',
                      fontFamily: 'Rajdhani, sans-serif',
                      fontSize: '14px',
                      outline: 'none',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#FF6B35'
                      e.currentTarget.style.boxShadow = '0 0 0 2px rgba(255, 107, 53, 0.1)'
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(138, 149, 170, 0.3)'
                      e.currentTarget.style.boxShadow = 'none'
                    }}
                  >
                    <option value="viewer">Viewer</option>
                    <option value="user">User</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                {/* Active Status */}
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  cursor: 'pointer',
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '14px',
                  color: '#FFFFFF'
                }}>
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))}
                    style={{
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer'
                    }}
                  />
                  <span style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>Active User</span>
                </label>

                {/* Action Buttons */}
                <div style={{
                  display: 'flex',
                  gap: '12px',
                  marginTop: '8px'
                }}>
                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      flex: 1,
                      background: 'rgba(107, 207, 127, 0.1)',
                      border: '2px solid rgba(107, 207, 127, 0.4)',
                      borderRadius: '6px',
                      padding: '12px 20px',
                      color: '#6BCF7F',
                      fontFamily: 'Rajdhani, sans-serif',
                      fontSize: '14px',
                      fontWeight: 700,
                      letterSpacing: '1px',
                      textTransform: 'uppercase',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      transition: 'all 0.2s',
                      opacity: loading ? 0.5 : 1
                    }}
                    onMouseEnter={(e) => {
                      if (!loading) {
                        e.currentTarget.style.background = 'rgba(107, 207, 127, 0.2)'
                        e.currentTarget.style.boxShadow = '0 0 15px rgba(107, 207, 127, 0.3)'
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(107, 207, 127, 0.1)'
                      e.currentTarget.style.boxShadow = 'none'
                    }}
                  >
                    {loading ? (
                      <Loader2 style={{ width: 16, height: 16 }} className="animate-spin" />
                    ) : (
                      <Save style={{ width: 16, height: 16 }} />
                    )}
                    {editingUser ? 'UPDATE USER' : 'CREATE USER'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateDialog(false)
                      setEditingUser(null)
                      setFormData({ name: '', email: '', role: 'user', active: true })
                    }}
                    style={{
                      background: 'rgba(138, 149, 170, 0.1)',
                      border: '2px solid rgba(138, 149, 170, 0.3)',
                      borderRadius: '6px',
                      padding: '12px 20px',
                      color: '#8A95AA',
                      fontFamily: 'Rajdhani, sans-serif',
                      fontSize: '14px',
                      fontWeight: 700,
                      letterSpacing: '1px',
                      textTransform: 'uppercase',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(138, 149, 170, 0.2)'
                      e.currentTarget.style.boxShadow = '0 0 15px rgba(138, 149, 170, 0.3)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(138, 149, 170, 0.1)'
                      e.currentTarget.style.boxShadow = 'none'
                    }}
                  >
                    <X style={{ width: 16, height: 16 }} />
                    CANCEL
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
